import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRestaurantById, getRestaurantRatingSummary } from '../services/restaurantService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ReservationForm from '../components/ReservationForm';
import './RestaurantDetail.css';

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({ average: 0, count: 0 });

  useEffect(() => {
    fetchRestaurant();
  }, [id]);

  const fetchRestaurant = async () => {
    try {
      setLoading(true);
      const data = await getRestaurantById(id);
      setRestaurant(data);
      // fetch rating summary
      const s = await getRestaurantRatingSummary(id);
      setSummary(s);
      setError('');
    } catch (err) {
      setError('Failed to load restaurant details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="restaurant-detail-container">
      <Navbar />

      <div className="container py-4">
        {error && (
          <div className="error-alert" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : restaurant ? (
          <div className="detail-grid">
            {/* Left Column - Reservation Form */}
            <div className="reservation-column">
              <div className="reservation-sticky">
                <ReservationForm
                  restaurantId={parseInt(id)}
                  restaurantName={restaurant.name}
                  restaurant={restaurant}
                />
              </div>
            </div>

            {/* Right Column - Restaurant Info */}
            <div className="info-column">
              <div className="detail-card">
                {/* Restaurant Name - Above Image */}
                <div className="restaurant-header">
                  <h1 className="restaurant-name">{restaurant.name}</h1>
                </div>

                {/* Restaurant Image */}
                <div className="restaurant-image-section">
                  {restaurant.imageUrl ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${restaurant.imageUrl}`}
                      alt={restaurant.name}
                      className="restaurant-image"
                    />
                  ) : (
                    <div className="image-placeholder">
                      <i className="bi bi-image-fill"></i>
                    </div>
                  )}
                </div>
                {/* Social Media Icons - Refined UI Below Image */}
                {(restaurant.instagramUrl || restaurant.spotifyUrl) && (
                  <div className="social-media-section" aria-label="Social media links">
                    <h3 className="social-media-title">Join us on social media</h3>
                    <div className="social-media-inner">
                      {restaurant.instagramUrl && (
                        <a
                          href={restaurant.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-icon-link instagram"
                          title="Follow on Instagram"
                        >
                          <span className="visually-hidden">Instagram</span>
                          <i className="bi bi-instagram"></i>
                        </a>
                      )}
                      {restaurant.spotifyUrl && (
                        <a
                          href={restaurant.spotifyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-icon-link spotify"
                          title="Listen on Spotify"
                        >
                          <span className="visually-hidden">Spotify</span>
                          <i className="bi bi-spotify"></i>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Rating Summary Card */}
                <div className="rating-card">
                  <div className="average-rating-compact">
                    <div className="rating-number">
                      {summary.count > 0
                        ? (summary.average?.toFixed ? summary.average.toFixed(1) : summary.average)
                        : '4.5'}
                    </div>
                    <div className="stars-display">
                      {Array.from({ length: 5 }).map((_, i) => {
                        const rating = summary.count > 0 ? summary.average : 4.5;
                        const starPosition = i + 1;
                        let starClass = 'bi-star'; // empty star
                        if (rating >= starPosition) {
                          starClass = 'bi-star-fill'; // full star
                        } else if (rating >= starPosition - 0.5) {
                          starClass = 'bi-star-half'; // half star
                        }
                        return (
                          <i
                            key={i}
                            className={`bi ${starClass}`}
                          />
                        );
                      })}
                    </div>
                    {summary.count > 0 && (
                      <div className="rating-count">
                        {summary.count} reviews
                      </div>
                    )}
                  </div>
                </div>

                {/* Location */}
                {restaurant.location && (
                  <div
                    className="location-card-wrapper"
                    onClick={() => {
                      // Use locationUrl if available, otherwise use Google Maps search
                      const mapUrl = restaurant.locationUrl
                        ? restaurant.locationUrl
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.location)}`;
                      window.open(mapUrl, '_blank');
                    }}
                  >
                    <div className="location-card">
                      <div className="location-icon-modern">
                        <div className="icon-circle">
                          <i className="bi bi-geo-alt-fill"></i>
                        </div>
                      </div>
                      <div className="location-info">
                        <div className="location-text">{restaurant.location}</div>
                        <div className="location-action-text">
                          <i className="bi bi-arrow-right-circle-fill me-2"></i>
                          View on Map
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description with Category */}
                {restaurant.description && (
                  <div className="description-card">
                    <h3 className="section-title2">
                      <i className="bi bi-stars me-2"></i>
                      Experience
                    </h3>
                    {restaurant.category && (
                      <div className="category-tag">
                        <i className="bi bi-tag-fill me-2"></i>
                        {restaurant.category}
                      </div>
                    )}
                    <p className="description-text">{restaurant.description}</p>
                  </div>
                )}

                {/* Contact Us - Phone Numbers */}
                {restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0 && (
                  <div className="contact-card">
                    <h3 className="contact-title">Contact Us</h3>
                    <div className="phone-numbers-list">
                      {restaurant.phoneNumbers.map((phone, index) => (
                        <div key={index} className="phone-number-row">
                          <i className="bi bi-telephone-fill phone-icon"></i>
                          <span className="phone-number-text">{phone}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button className="back-btn-bottom" onClick={() => navigate('/')}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back to Restaurants
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <h3>Restaurant not found</h3>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RestaurantDetail;
